import { Controller } from "@hotwired/stimulus"
import { BOUND_KEYS, filterSource } from "../../utils/event_filter_source"

const MORE_SELECTOR = "[data-more-anchor]"
const MOBILE_HIDDEN = "max-lg:hidden"
const CARD_UID = "__UID__"
const IDLE_DEBOUNCE = 300
const DESKTOP_QUERY = "(min-width: 1024px)"

export default class extends Controller {
  static targets = [
    "cards",
    "countArea", "areaChip", "areaClear",
    "unmappedRow", "unmappedToggle",
    "cappedNotice", "emptyAreaNotice",
    "filterBar", "idle", "listCol", "mapCol", "mapPane",
    "mobileIcon", "mobileLabel",
  ]

  static outlets = ["event-map"]
  static values = {
    mapLabel: String, listLabel: String, cardUrl: String,
    cappedLabel: String, emptyAreaLabel: String,
  }

  // targetConnected fires before connect(), so every flag it reads exists by initialize().
  initialize() {
    this.mapView = false
    this.mobileList = true
    this.includeUnmapped = false
    this.pending = null
    this.mappable = null
    this.hoverUid = null
    this.gen = 0
    this.boundsSource = "viewport"
    this.viewportBox = null
    this.clusterBox = null
    this.fetchedKey = null
    this.capped = false
    this.emptyArea = false
    this.idleTimer = null
    this.holdFirstBounds = false
    this.cardCache = new Map()
    this.observer = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) this.loadNext() },
      { rootMargin: "400px" },
    )
  }

  disconnect() {
    this.observer.disconnect()
    if (this.idleTimer) clearTimeout(this.idleTimer)
  }

  // The filter frame re-renders the toolbar unchecked, so restore the live state.
  unmappedToggleTargetConnected(element) {
    element.checked = this.includeUnmapped
  }

  // The filter frame re-renders the list column from the server, dropping the
  // class the mobile toggle put on it.
  listColTargetConnected() {
    this.applyMobileView()
  }

  // Fresh server markup means a new filter result, so any in-flight page fetch and
  // the bounds it was narrowed to are stale.
  cardsTargetConnected() {
    this.gen += 1
    this.pending = null
    this.boundsSource = "viewport"
    this.viewportBox = null
    this.clusterBox = null
    this.fetchedKey = null
    this.capped = false
    this.emptyArea = false
    this.renderNotices()
    this.observeMore()
  }

  // --- paging ----------------------------------------------------------------

  get moreAnchor() {
    return this.hasCardsTarget ? this.cardsTarget.querySelector(MORE_SELECTOR) : null
  }

  observeMore() {
    this.observer.disconnect()
    const anchor = this.moreAnchor
    if (anchor) this.observer.observe(anchor)
  }

  loadMoreClick(event) {
    event.preventDefault()
    this.loadNext()
  }

  loadNext() {
    if (this.pending) return this.pending
    if (this.idleTimer) return Promise.resolve(false)
    const anchor = this.moreAnchor
    if (!anchor) return Promise.resolve(false)

    const url = new URL(anchor.href, window.location.href)
    url.searchParams.set("partial", "1")
    this.applyBounds(url)
    anchor.setAttribute("aria-busy", "true")
    const gen = this.gen

    const request = fetch(url.toString(), { headers: { Accept: "text/html" } })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error("Could not load more events"))))
      .then((html) => {
        if (gen !== this.gen) return false
        const tmpl = document.createElement("template")
        tmpl.innerHTML = html.trim()
        anchor.remove()
        this.cardsTarget.appendChild(tmpl.content)
        this.observeMore()
        return true
      })
      .catch(() => {
        anchor.removeAttribute("aria-busy")
        return false
      })

    this.pending = request.then((ok) => {
      if (gen === this.gen) this.pending = null
      return ok
    })
    return this.pending
  }

  // --- bounds-driven list ----------------------------------------------------

  get activeBox() {
    return this.boundsSource === "cluster" ? this.clusterBox : this.viewportBox
  }

  setViewportBounds(box) {
    // An auto-revealed map must not narrow a listing nobody asked to narrow, so
    // the fit that follows the reveal is dropped, box and all. Paging stays
    // unbounded until the reader moves the map themselves.
    if (this.holdFirstBounds) { this.holdFirstBounds = false; return }
    this.viewportBox = box
    if (this.boundsSource !== "viewport") return
    if (this.idleTimer) clearTimeout(this.idleTimer)
    this.idleTimer = setTimeout(() => { this.idleTimer = null; this.refetch() }, IDLE_DEBOUNCE)
  }

  setClusterBounds(box) {
    if (!box) return
    this.boundsSource = "cluster"
    this.clusterBox = box
    this.refetch(true)
  }

  // Dismissing a selection falls back to the viewport box, so the list keeps
  // agreeing with the map. A refit that does not actually move fires no idle,
  // so the refetch cannot wait for one.
  clearClusterBounds() {
    if (this.boundsSource !== "cluster") return
    this.boundsSource = "viewport"
    this.clusterBox = null
    this.refetch()
  }

  // Resetting the area drops every box. A posted fit from a viewport narrowing
  // always moves the map, so its idle does the refetch instead.
  clearAreaBounds(refit) {
    const wasCluster = this.boundsSource === "cluster"
    this.boundsSource = "viewport"
    this.clusterBox = null
    this.viewportBox = null
    if (wasCluster || !refit) this.refetch()
  }

  boundsKey() {
    const box = this.activeBox
    const flag = this.includeUnmapped ? "1" : "0"
    if (!box) return `none:${flag}`
    return `${box.sw.lat},${box.sw.lng},${box.ne.lat},${box.ne.lng}:${flag}`
  }

  applyBounds(url) {
    const box = this.activeBox
    if (!box) {
      BOUND_KEYS.forEach((key) => url.searchParams.delete(key))
      url.searchParams.delete("unmapped")
      return
    }
    url.searchParams.set("n", String(box.ne.lat))
    url.searchParams.set("s", String(box.sw.lat))
    url.searchParams.set("e", String(box.ne.lng))
    url.searchParams.set("w", String(box.sw.lng))
    if (this.includeUnmapped) url.searchParams.set("unmapped", "1")
    else url.searchParams.delete("unmapped")
  }

  listUrl() {
    const url = filterSource()
    url.searchParams.set("partial", "1")
    url.searchParams.delete("page")
    this.applyBounds(url)
    return url
  }

  refetch(scrollTop) {
    if (!this.hasCardsTarget || !this.mapView) return Promise.resolve(false)
    const key = this.boundsKey()
    if (key === this.fetchedKey) {
      this.observeMore()
      return Promise.resolve(false)
    }
    this.fetchedKey = key
    this.gen += 1
    this.pending = null
    const gen = this.gen

    return fetch(this.listUrl().toString(), { headers: { Accept: "text/html" } })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error("Could not load events"))))
      .then((html) => {
        if (gen !== this.gen) {
          if (this.fetchedKey === key) this.fetchedKey = null
          return false
        }
        this.cardsTarget.innerHTML = html
        this.emptyArea = Boolean(this.activeBox) && !this.cardsTarget.querySelector("[data-uid]")
        this.observeMore()
        this.renderCounts()
        this.renderNotices()
        if (scrollTop) this.scrollListTop()
        return true
      })
      .catch(() => {
        if (this.fetchedKey === key) this.fetchedKey = null
        return false
      })
  }

  // The filter bar is sticky, so aligning the list with the raw viewport top
  // parks the first row of cards underneath it.
  scrollListTop() {
    if (!this.hasListColTarget) return
    const top = this.listColTarget.getBoundingClientRect().top + window.scrollY - this.stickyOffset()
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" })
  }

  stickyOffset() {
    if (!this.hasFilterBarTarget) return 0
    if (getComputedStyle(this.filterBarTarget).position !== "sticky") return 0
    return this.filterBarTarget.offsetHeight
  }

  // --- map reveal ------------------------------------------------------------

  // Fires for an outlet already present when this controller connects, so it is
  // the one hook guaranteed to see the map controller whichever connects first.
  eventMapOutletConnected() {
    this.autoReveal()
  }

  // A map load is billed per page view, so desktop only skips the click when a
  // person is actually looking: a prerendered or backgrounded tab boots nothing,
  // and neither does a headless browser.
  autoReveal() {
    if (this.mapView || !this.hasEventMapOutlet) return
    if (!window.matchMedia(DESKTOP_QUERY).matches) return
    if (navigator.webdriver) return
    if (document.visibilityState !== "visible") {
      document.addEventListener("visibilitychange", () => this.autoReveal(), { once: true })
      return
    }

    this.holdFirstBounds = true
    this.revealMap()
  }

  showMap(event) {
    if (event) event.preventDefault()
    this.mobileList = false
    this.revealMap()
  }

  // Auto-load leaves mobileList alone: the map column is hidden below lg anyway,
  // and flipping it would drop a narrowing desktop window onto the map.
  revealMap() {
    const firstReveal = !this.mapView
    this.mapView = true
    this.applyMobileView()
    if (!firstReveal) return

    if (this.hasIdleTarget) this.idleTarget.classList.add("hidden")
    if (this.hasMapPaneTarget) this.mapPaneTarget.classList.remove("hidden")

    if (this.hasEventMapOutlet) this.eventMapOutlet.reveal()
  }

  // The floating pill swaps the list and the map below lg; the map is only ever
  // hidden by a class, so the iframe mounts once and survives every toggle.
  toggleMobileView(event) {
    if (event) event.preventDefault()
    if (!this.mapView) { this.showMap(); return }
    this.mobileList = !this.mobileList
    this.applyMobileView()
  }

  applyMobileView() {
    const showingMap = this.mapView && !this.mobileList
    if (this.hasMapColTarget) this.mapColTarget.classList.toggle(MOBILE_HIDDEN, !showingMap)
    if (this.hasListColTarget) this.listColTarget.classList.toggle(MOBILE_HIDDEN, showingMap)
    if (this.hasMobileLabelTarget) this.mobileLabelTarget.textContent = showingMap ? this.listLabelValue : this.mapLabelValue
    if (this.hasMobileIconTarget) this.mobileIconTarget.className = showingMap ? "fa-solid fa-list text-sm" : "fa-solid fa-map text-sm"
    if (showingMap && this.hasEventMapOutlet) this.eventMapOutlet.remeasure()
  }

  // --- counts ----------------------------------------------------------------

  // Called by the event-map controller whenever the markers, the viewport bounds
  // or the cluster selection change.
  applyViewport({ mappable, unmappedCount, areaText, narrowed }) {
    this.mappable = mappable
    this.unmappedCount = unmappedCount
    this.areaText = areaText
    this.narrowed = narrowed
    this.renderCounts()
  }

  // The headline total is filter-scoped and server-rendered, so a bounded refetch never rewrites it.
  renderCounts() {
    if (this.hasCountAreaTarget) this.countAreaTarget.textContent = this.areaText || ""
    if (this.hasAreaChipTarget) {
      this.areaChipTarget.classList.toggle("hidden", !this.areaText)
      this.areaChipTarget.disabled = !this.narrowed
    }
    if (this.hasAreaClearTarget) this.areaClearTarget.classList.toggle("hidden", !this.narrowed)
    if (this.hasUnmappedRowTarget) {
      this.unmappedRowTarget.classList.toggle("hidden", !(this.mapView && this.unmappedCount > 0))
    }
  }

  // The cap describes the filter set, not the viewport, so a bounds refetch leaves it alone.
  setCapped(capped) {
    this.capped = Boolean(capped)
    this.renderNotices()
  }

  renderNotices() {
    if (this.hasCappedNoticeTarget) {
      this.cappedNoticeTarget.textContent = this.cappedLabelValue
      this.cappedNoticeTarget.classList.toggle("hidden", !this.capped)
    }
    if (this.hasEmptyAreaNoticeTarget) {
      this.emptyAreaNoticeTarget.textContent = this.emptyAreaLabelValue
      this.emptyAreaNoticeTarget.classList.toggle("hidden", !this.emptyArea)
    }
  }

  resetArea(event) {
    if (event) event.preventDefault()
    if (this.hasEventMapOutlet) this.eventMapOutlet.resetArea()
  }

  toggleUnmapped() {
    this.includeUnmapped = this.hasUnmappedToggleTarget ? this.unmappedToggleTarget.checked : !this.includeUnmapped
    this.refetch()
  }

  // --- info window card ------------------------------------------------------

  // The map info window renders the same card as the list; the list itself is
  // never touched by a marker click.
  fetchCard(uid) {
    const key = String(uid)
    if (this.cardCache.has(key)) return Promise.resolve(this.cardCache.get(key))
    const template = this.cardUrlValue
    if (!template) return Promise.resolve("")
    const url = template.replace(CARD_UID, encodeURIComponent(key))
    return fetch(url, { headers: { Accept: "text/html" } })
      .then((res) => (res.ok ? res.text() : ""))
      .then((html) => {
        const trimmed = html.trim()
        if (trimmed) this.cardCache.set(key, trimmed)
        return trimmed
      })
      .catch(() => "")
  }

  // --- two-way card/marker focus --------------------------------------------

  cardNode(uid) {
    if (!uid || !this.hasCardsTarget) return null
    const escaped = window.CSS && CSS.escape ? CSS.escape(uid) : String(uid).replace(/["\\]/g, "\\$&")
    return this.cardsTarget.querySelector(`[data-uid="${escaped}"]`)
  }

  focusCard(uid, scroll) {
    const node = this.cardNode(uid)
    if (!node) return
    node.classList.add("em-card-active")
    if (scroll) node.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  unfocusCard(uid) {
    const node = this.cardNode(uid)
    if (node) node.classList.remove("em-card-active")
  }

  // mouseenter does not bubble, so hover is delegated off the card grid.
  cardOver(event) {
    const card = event.target.closest("[data-uid]")
    const uid = card ? card.dataset.uid : null
    if (uid === this.hoverUid) return
    this.leaveCard()
    if (!uid) return
    this.hoverUid = uid
    if (this.mappable && this.mappable.has(uid) && this.hasEventMapOutlet) this.eventMapOutlet.highlightMarker(uid)
  }

  cardOut(event) {
    if (event.relatedTarget && this.cardsTarget.contains(event.relatedTarget)) return
    this.leaveCard()
  }

  leaveCard() {
    if (!this.hoverUid) return
    if (this.hasEventMapOutlet) this.eventMapOutlet.unhighlightMarker(this.hoverUid)
    this.hoverUid = null
  }
}
